import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const LinkedListContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LinkedListVisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const LinkedListTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const LinkedListVisualization = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow-x: auto;
`;

const NodeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 20px;
`;

const Node = styled.div`
  background: ${props => {
    if (props.isHighlighted) return '#ff6b6b';
    if (props.isInserting) return '#4ecdc4';
    if (props.isDeleting) return '#ffd43b';
    if (props.isTraversing) return '#51cf66';
    if (props.isSearching) return '#ffa500';
    return '#f8f9fa';
  }};
  border: 2px solid ${props => props.isHead ? '#51cf66' : props.isTail ? '#ff6b6b' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #333;
  font-size: 1.2rem;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
`;

const NodeLabel = styled.div`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
`;

const Arrow = styled.div`
  color: white;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 10px;
  transition: all 0.3s ease;
  
  ${props => props.isHighlighted && `
    color: #ff6b6b;
    transform: scale(1.2);
  `}
`;

const NullPointer = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  font-weight: bold;
  font-style: italic;
  margin-left: 20px;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid ${props => props.borderColor || 'transparent'};
`;

const Controls = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ControlGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  background: ${props => props.primary ? '#51cf66' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-bottom: 10px;
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? '#40c057' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AlgorithmInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
  height: fit-content;
`;

const InfoTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const InfoText = styled.p`
  margin: 0 0 15px 0;
  line-height: 1.6;
  opacity: 0.9;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const OperationStatus = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  text-align: center;
  color: ${props => props.success ? '#51cf66' : props => props.error ? '#ff6b6b' : 'white'};
  font-weight: 500;
`;

// Node class for linked list
class ListNode {
  constructor(value) {
    this.value = value;
    this.next = null;
  }
}

function LinkedListPage() {
  const [linkedList, setLinkedList] = useState([]);
  const [head, setHead] = useState(null);
  const [insertValue, setInsertValue] = useState('');
  const [insertIndex, setInsertIndex] = useState('');
  const [deleteIndex, setDeleteIndex] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [currentOperation, setCurrentOperation] = useState('');
  const [isOperating, setIsOperating] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [operationSpeed, setOperationSpeed] = useState(500);
  const [operationCount, setOperationCount] = useState(0);
  const [lastOperation, setLastOperation] = useState('');

  const generateRandomLinkedList = useCallback((size) => {
    const values = Array.from({ length: size }, () => Math.floor(Math.random() * 90) + 10);
    const nodes = values.map(value => new ListNode(value));
    
    // Connect nodes
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i].next = nodes[i + 1];
    }
    
    setLinkedList(nodes);
    setHead(nodes[0]);
  }, []);

  useEffect(() => {
    generateRandomLinkedList(5);
  }, [generateRandomLinkedList]);

  const resetOperation = () => {
    setHighlightedIndex(-1);
    setIsOperating(false);
    setCurrentOperation('');
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const insertAtBeginning = async () => {
    if (isOperating || !insertValue) return;
    
    const value = parseInt(insertValue);
    setIsOperating(true);
    setCurrentOperation('Inserting at beginning...');
    setOperationCount(prev => prev + 1);
    
    const newNode = new ListNode(value);
    newNode.next = head;
    
    // Animate insertion
    setHighlightedIndex(0);
    await delay(1000 / operationSpeed);
    
    setHead(newNode);
    setLinkedList([newNode, ...linkedList]);
    setInsertValue('');
    
    setLastOperation(`Inserted ${value} at beginning`);
    resetOperation();
  };

  const insertAtEnd = async () => {
    if (isOperating || !insertValue) return;
    
    const value = parseInt(insertValue);
    setIsOperating(true);
    setCurrentOperation('Inserting at end...');
    setOperationCount(prev => prev + 1);
    
    const newNode = new ListNode(value);
    
    if (!head) {
      setHead(newNode);
      setLinkedList([newNode]);
    } else {
      // Animate to end
      for (let i = 0; i < linkedList.length; i++) {
        setHighlightedIndex(i);
        await delay(1000 / operationSpeed);
      }
      
      linkedList[linkedList.length - 1].next = newNode;
      setLinkedList([...linkedList, newNode]);
    }
    
    setInsertValue('');
    setLastOperation(`Inserted ${value} at end`);
    resetOperation();
  };

  const insertAtIndex = async () => {
    if (isOperating || !insertValue || insertIndex === '') return;
    
    const value = parseInt(insertValue);
    const index = parseInt(insertIndex);
    
    if (index < 0 || index > linkedList.length) {
      setLastOperation('Error: Invalid index');
      return;
    }
    
    setIsOperating(true);
    setCurrentOperation('Inserting at index...');
    setOperationCount(prev => prev + 1);
    
    const newNode = new ListNode(value);
    
    if (index === 0) {
      newNode.next = head;
      setHead(newNode);
      setLinkedList([newNode, ...linkedList]);
    } else {
      // Traverse to position
      for (let i = 0; i < index; i++) {
        setHighlightedIndex(i);
        await delay(1000 / operationSpeed);
      }
      
      const prevNode = linkedList[index - 1];
      newNode.next = prevNode.next;
      prevNode.next = newNode;
      
      const newList = [...linkedList];
      newList.splice(index, 0, newNode);
      setLinkedList(newList);
    }
    
    setInsertValue('');
    setInsertIndex('');
    setLastOperation(`Inserted ${value} at index ${index}`);
    resetOperation();
  };

  const deleteAtIndex = async () => {
    if (isOperating || deleteIndex === '') return;
    
    const index = parseInt(deleteIndex);
    
    if (index < 0 || index >= linkedList.length) {
      setLastOperation('Error: Invalid index');
      return;
    }
    
    setIsOperating(true);
    setCurrentOperation('Deleting...');
    setOperationCount(prev => prev + 1);
    
    // Traverse to position
    for (let i = 0; i < index; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
    }
    
    setHighlightedIndex(index);
    await delay(1000 / operationSpeed);
    
    const deletedValue = linkedList[index].value;
    
    if (index === 0) {
      setHead(linkedList[1]);
      setLinkedList(linkedList.slice(1));
    } else {
      const prevNode = linkedList[index - 1];
      prevNode.next = linkedList[index + 1];
      
      const newList = linkedList.filter((_, i) => i !== index);
      setLinkedList(newList);
    }
    
    setDeleteIndex('');
    setLastOperation(`Deleted ${deletedValue} from index ${index}`);
    resetOperation();
  };

  const searchElement = async () => {
    if (isOperating || !searchValue) return;
    
    const targetValue = parseInt(searchValue);
    setIsOperating(true);
    setCurrentOperation('Searching...');
    setOperationCount(prev => prev + 1);
    
    // Linear search with animation
    for (let i = 0; i < linkedList.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
      
      if (linkedList[i].value === targetValue) {
        setLastOperation(`Found ${targetValue} at index ${i}`);
        resetOperation();
        return;
      }
    }
    
    setLastOperation(`${targetValue} not found in linked list`);
    resetOperation();
  };

  const traverseList = async () => {
    if (isOperating) return;
    
    setIsOperating(true);
    setCurrentOperation('Traversing...');
    setOperationCount(prev => prev + 1);
    
    // Animate through each element
    for (let i = 0; i < linkedList.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
    }
    
    setLastOperation('Linked list traversal completed');
    resetOperation();
  };

  const reverseList = async () => {
    if (isOperating) return;
    
    setIsOperating(true);
    setCurrentOperation('Reversing...');
    setOperationCount(prev => prev + 1);
    
    let prev = null;
    let current = head;
    let next = null;
    
    // Animate reversal
    for (let i = 0; i < linkedList.length; i++) {
      setHighlightedIndex(i);
      await delay(1000 / operationSpeed);
      
      next = current.next;
      current.next = prev;
      prev = current;
      current = next;
    }
    
    setHead(prev);
    
    // Update linkedList array
    const newList = [];
    let node = prev;
    while (node) {
      newList.push(node);
      node = node.next;
    }
    setLinkedList(newList);
    
    setLastOperation('Linked list reversed successfully');
    resetOperation();
  };

  return (
    <LinkedListContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Linked List Visualizer</Title>
        <Subtitle>Learn and visualize various linked list operations with interactive animations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <LinkedListVisualizationContainer>
            <LinkedListTitle>Linked List Visualization</LinkedListTitle>
            <LinkedListVisualization>
              {linkedList.length === 0 ? (
                <div style={{ color: 'white', fontSize: '1.2rem', textAlign: 'center' }}>
                  Empty Linked List
                </div>
              ) : (
                <NodeContainer>
                  {linkedList.map((node, index) => (
                    <React.Fragment key={index}>
                      <Node
                        isHead={index === 0}
                        isTail={index === linkedList.length - 1}
                        isHighlighted={index === highlightedIndex}
                        isInserting={currentOperation.includes('Inserting') && index === highlightedIndex}
                        isDeleting={currentOperation.includes('Deleting') && index === highlightedIndex}
                        isTraversing={currentOperation.includes('Traversing') && index === highlightedIndex}
                        isSearching={currentOperation.includes('Searching') && index === highlightedIndex}
                      >
                        {node.value}
                        <NodeLabel>
                          {index === 0 ? 'HEAD' : index === linkedList.length - 1 ? 'TAIL' : `Node ${index}`}
                        </NodeLabel>
                      </Node>
                      {index < linkedList.length - 1 && (
                        <Arrow isHighlighted={index === highlightedIndex}>→</Arrow>
                      )}
                    </React.Fragment>
                  ))}
                  <NullPointer>NULL</NullPointer>
                </NodeContainer>
              )}
            </LinkedListVisualization>
            
            <Legend>
              <LegendItem>
                <LegendColor color="#f8f9fa" borderColor="rgba(255, 255, 255, 0.3)" />
                <span>Normal Node</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#51cf66" borderColor="#51cf66" />
                <span>HEAD</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff6b6b" borderColor="#ff6b6b" />
                <span>TAIL</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff6b6b" />
                <span>Highlighted</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#4ecdc4" />
                <span>Inserting</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffd43b" />
                <span>Deleting</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#51cf66" />
                <span>Traversing</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffa500" />
                <span>Searching</span>
              </LegendItem>
            </Legend>
          </LinkedListVisualizationContainer>
          
          <Controls>
            <ControlGroup>
              <Label>Operation Speed:</Label>
              <Input
                type="range"
                min="100"
                max="1000"
                value={operationSpeed}
                onChange={(e) => setOperationSpeed(parseInt(e.target.value))}
                disabled={isOperating}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{operationSpeed}ms</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Insert Element:</Label>
              <Input
                type="number"
                value={insertValue}
                onChange={(e) => setInsertValue(e.target.value)}
                placeholder="Value"
                disabled={isOperating}
                style={{ marginBottom: '10px' }}
              />
              <Input
                type="number"
                value={insertIndex}
                onChange={(e) => setInsertIndex(e.target.value)}
                placeholder="Index (optional)"
                disabled={isOperating}
                style={{ marginBottom: '10px' }}
              />
              <div>
                <Button 
                  onClick={insertAtBeginning} 
                  disabled={isOperating || !insertValue}
                >
                  Insert at Start
                </Button>
                <Button 
                  onClick={insertAtEnd} 
                  disabled={isOperating || !insertValue}
                >
                  Insert at End
                </Button>
                <Button 
                  onClick={insertAtIndex} 
                  disabled={isOperating || !insertValue || insertIndex === ''}
                >
                  Insert at Index
                </Button>
              </div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Delete Element:</Label>
              <Input
                type="number"
                value={deleteIndex}
                onChange={(e) => setDeleteIndex(e.target.value)}
                placeholder="Index"
                disabled={isOperating}
              />
              <Button 
                onClick={deleteAtIndex} 
                disabled={isOperating || deleteIndex === ''}
                style={{ marginTop: '10px' }}
              >
                Delete at Index
              </Button>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Search Element:</Label>
              <Input
                type="number"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Value to search"
                disabled={isOperating}
              />
              <Button 
                onClick={searchElement} 
                disabled={isOperating || !searchValue}
                style={{ marginTop: '10px' }}
              >
                Search
              </Button>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Linked List Operations:</Label>
              <div>
                <Button onClick={traverseList} disabled={isOperating}>
                  Traverse
                </Button>
                <Button onClick={reverseList} disabled={isOperating}>
                  Reverse
                </Button>
              </div>
            </ControlGroup>
            
            <ControlGroup>
              <Button onClick={() => generateRandomLinkedList(5)} disabled={isOperating}>
                Generate New List
              </Button>
            </ControlGroup>
          </Controls>
        </LeftPanel>
        
        <AlgorithmInfo>
          <InfoTitle>Linked List Operations</InfoTitle>
          
          <InfoText>
            <strong>Current Operation:</strong> {currentOperation || 'Ready'}
          </InfoText>
          
          <InfoText>
            <strong>List Length:</strong> {linkedList.length}
          </InfoText>
          
          <InfoText>
            <strong>Available Operations:</strong>
          </InfoText>
          
          <InfoText>
            • <strong>Insert at Start:</strong> Add element at beginning (O(1))
          </InfoText>
          
          <InfoText>
            • <strong>Insert at End:</strong> Add element at end (O(n))
          </InfoText>
          
          <InfoText>
            • <strong>Insert at Index:</strong> Add element at specific position
          </InfoText>
          
          <InfoText>
            • <strong>Delete at Index:</strong> Remove element from specific position
          </InfoText>
          
          <InfoText>
            • <strong>Search:</strong> Find element using linear search (O(n))
          </InfoText>
          
          <InfoText>
            • <strong>Traverse:</strong> Visit each element sequentially
          </InfoText>
          
          <InfoText>
            • <strong>Reverse:</strong> Reverse the linked list order
          </InfoText>
          
          <StatsContainer>
            <StatBox>
              <StatValue>{operationCount}</StatValue>
              <StatLabel>Operations</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{linkedList.length}</StatValue>
              <StatLabel>Nodes</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {lastOperation && (
            <OperationStatus 
              success={!lastOperation.includes('Error')}
              error={lastOperation.includes('Error')}
            >
              {lastOperation}
            </OperationStatus>
          )}
        </AlgorithmInfo>
      </MainContent>
    </LinkedListContainer>
  );
}

export default LinkedListPage;
